import RAPIER from '@dimforge/rapier3d-compat';
import type { Vec3 } from '../vec.js';

/**
 * Rapier wrapper. The ONLY module in core/ that touches the physics engine.
 *
 * Determinism notes (tech/architecture.md calls this out as the named risk):
 *  - one pinned WASM build (@dimforge/rapier3d-compat, exact version in package.json),
 *  - fixed 120 Hz timestep, never a variable dt,
 *  - non-SIMD build: WASM float arithmetic is bit-deterministic by spec, so a same-build
 *    twin run must match exactly; we hash raw float bits rather than quantizing so that
 *    any drift is loud instead of silent,
 *  - we never iterate Rapier's internal body sets; ordering always comes from our own
 *    ascending-id arrays.
 */

/** Collision layers. SHELL is the cabinet itself — even a ghost cannot leave the room. */
export const LAYER = {
  SHELL: 0x0001,
  FIELD: 0x0002,
  TOKEN: 0x0004,
} as const;

export const groups = (membership: number, filter: number): number =>
  (((membership & 0xffff) << 16) | (filter & 0xffff)) >>> 0;

const GROUPS_SHELL = groups(LAYER.SHELL, LAYER.TOKEN);
const GROUPS_FIELD = groups(LAYER.FIELD, LAYER.TOKEN);
const GROUPS_TOKEN = groups(LAYER.TOKEN, LAYER.SHELL | LAYER.FIELD | LAYER.TOKEN);
/** A phasing token still respects the cabinet shell — otherwise it just leaves. */
const GROUPS_TOKEN_GHOST = groups(LAYER.TOKEN, LAYER.SHELL);

let inited = false;

export async function initPhysics(): Promise<void> {
  if (inited) return;
  await RAPIER.init();
  inited = true;
}

export function physicsReady(): boolean {
  return inited;
}

export interface TokenBodyOpts {
  mass: number;
  restitution: number;
  friction: number;
  radius: number;
  halfHeight: number;
  linearDamping?: number;
  angularDamping?: number;
}

export interface CollisionEvt {
  /** body handles; resolve to token ids via the sim's registry */
  a: number;
  b: number;
  /** true if either side is static field/shell geometry */
  aStatic: boolean;
  bStatic: boolean;
  /**
   * The static side is something a token can ANCHOR to: pegs, ledges, the pusher shelf,
   * already-stuck tokens. Cabinet walls, glass and the launch rail are not anchors —
   * [Kleber] sticking to the launch rail jams the machine and is nobody's fantasy.
   */
  anchor: boolean;
  impulse: number;
}

/**
 * `field`  — phaseable by [Geist], anchorable by [Kleber] (pegs, ledges, terrain)
 * `ground` — solid to everything (a ghost must not fall through the floor) but still
 *            anchorable, because a dam on the pusher shelf is the whole point
 * `shell`  — the cabinet itself: solid, never anchorable
 */
export type StaticLayer = 'shell' | 'field' | 'ground';

export type StaticShape =
  | { kind: 'box'; hx: number; hy: number; hz: number; pos: Vec3 }
  | { kind: 'cylinder'; halfHeight: number; radius: number; pos: Vec3 }
  | { kind: 'ball'; radius: number; pos: Vec3 };

export class PhysicsWorld {
  readonly world: RAPIER.World;
  private readonly eq: RAPIER.EventQueue;
  /** bodyHandle -> its (single) collider handle */
  private readonly colliderOfBody = new Map<number, number>();
  /** colliderHandle -> bodyHandle, or -1 for static geometry */
  private readonly bodyOfCollider = new Map<number, number>();
  /** static collider handles that are FIELD (phaseable) rather than SHELL */
  private readonly fieldColliders = new Set<number>();
  /** static collider handles a [Kleber] token may weld itself to */
  private readonly anchorColliders = new Set<number>();

  constructor(gravity: Vec3, timestep = 1 / 120) {
    if (!inited) throw new Error('initPhysics() must be awaited before creating a PhysicsWorld');
    this.world = new RAPIER.World(gravity);
    this.world.timestep = timestep;
    // Pinned solver settings. These are part of the determinism contract, not a tuning
    // knob to fiddle with: changing them changes every replay hash in existence.
    // 2 iterations instead of 4 costs ~20 % of the solver budget with no measurable
    // penetration in a settled coin pile (measured: lowest coin y identical to 4 dp).
    this.world.integrationParameters.numSolverIterations = 2;
    this.eq = new RAPIER.EventQueue(true);
  }

  setGravity(g: Vec3): void {
    this.world.gravity = g;
  }

  // ------------------------------------------------------------ static geometry

  addStatic(shape: StaticShape, layer: StaticLayer, friction = 0.4, restitution = 0.3): number {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(shape.pos.x, shape.pos.y, shape.pos.z),
    );
    const desc = staticDesc(shape)
      .setFriction(friction)
      .setRestitution(restitution)
      .setCollisionGroups(layer === 'field' ? GROUPS_FIELD : GROUPS_SHELL)
      .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    const col = this.world.createCollider(desc, body);
    this.bodyOfCollider.set(col.handle, -1);
    if (layer === 'field') this.fieldColliders.add(col.handle);
    if (layer !== 'shell') this.anchorColliders.add(col.handle);
    return col.handle;
  }

  /** Kinematic mover (the MÜNZSCHIEBER pusher bar). */
  addKinematicBox(hx: number, hy: number, hz: number, pos: Vec3): number {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(pos.x, pos.y, pos.z),
    );
    const col = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(hx, hy, hz)
        .setFriction(0.6)
        .setRestitution(0.0)
        .setCollisionGroups(GROUPS_SHELL)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      body,
    );
    this.colliderOfBody.set(body.handle, col.handle);
    this.bodyOfCollider.set(col.handle, body.handle);
    return body.handle;
  }

  moveKinematic(handle: number, pos: Vec3): void {
    const b = this.world.getRigidBody(handle);
    if (b) b.setNextKinematicTranslation(pos);
  }

  /**
   * A kinematic mover that also turns — the FLIPPER's bats.
   *
   * Restitution is a parameter because a flipper is not a wall: the bat has to *give* the
   * ball energy, and the only honest way to do that with a position-based kinematic body
   * is a lively surface plus the sweep of the bat itself.
   */
  addKinematicBoxPose(
    hx: number, hy: number, hz: number, pos: Vec3, friction = 0.4, restitution = 0.35,
  ): number {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(pos.x, pos.y, pos.z),
    );
    const col = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(hx, hy, hz)
        .setFriction(friction)
        .setRestitution(restitution)
        .setCollisionGroups(GROUPS_SHELL)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      body,
    );
    this.colliderOfBody.set(body.handle, col.handle);
    this.bodyOfCollider.set(col.handle, body.handle);
    return body.handle;
  }

  /** Move AND turn a kinematic body. `rot` is a quaternion about the board normal (z). */
  moveKinematicPose(handle: number, pos: Vec3, rot: [number, number, number, number]): void {
    const b = this.world.getRigidBody(handle);
    if (!b) return;
    b.setNextKinematicTranslation(pos);
    b.setNextKinematicRotation({ x: rot[0], y: rot[1], z: rot[2], w: rot[3] });
  }

  /**
   * Per-body gravity multiplier.
   *
   * This exists for exactly one reason and it is a physical one, not a cheat: a pinball
   * table is *raked*, and the component of gravity along a table raked by θ is `g·sin θ`.
   * The FLIPPER is modelled as a vertical board (like the pachinko field, so it reads in
   * the same diorama and reuses the same recess-and-glass construction), which means the
   * rake has to come back in as a scale. `sin(8°) ≈ 0.14` is a real table's geometry, not
   * a difficulty knob — and it is what makes the ball catchable by a human instead of
   * arriving at the flippers at nineteen units a second.
   */
  setGravityScale(handle: number, scale: number): void {
    const b = this.world.getRigidBody(handle);
    if (b) b.setGravityScale(scale, true);
  }

  // ------------------------------------------------------------ tokens

  addToken(pos: Vec3, vel: Vec3, o: TokenBodyOpts): number {
    const desc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(pos.x, pos.y, pos.z)
      .setLinvel(vel.x, vel.y, vel.z)
      .setLinearDamping(o.linearDamping ?? 0.08)
      .setAngularDamping(o.angularDamping ?? 0.35)
      .setCcdEnabled(true);
    const body = this.world.createRigidBody(desc);

    // density chosen so the collider's mass equals the requested mass exactly
    const volume = Math.PI * o.radius * o.radius * 2 * o.halfHeight;
    const density = o.mass / Math.max(volume, 1e-6);
    const col = this.world.createCollider(
      RAPIER.ColliderDesc.cylinder(o.halfHeight, o.radius)
        .setDensity(density)
        .setRestitution(o.restitution)
        .setFriction(o.friction)
        .setCollisionGroups(GROUPS_TOKEN)
        .setContactForceEventThreshold(0.35)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS | RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS),
      body,
    );
    this.colliderOfBody.set(body.handle, col.handle);
    this.bodyOfCollider.set(col.handle, body.handle);
    return body.handle;
  }

  removeBody(handle: number): void {
    const b = this.world.getRigidBody(handle);
    if (!b) return;
    const col = this.colliderOfBody.get(handle);
    if (col !== undefined) this.bodyOfCollider.delete(col);
    this.colliderOfBody.delete(handle);
    this.world.removeRigidBody(b);
  }

  setGhost(handle: number, on: boolean): void {
    const colH = this.colliderOfBody.get(handle);
    if (colH === undefined) return;
    const col = this.world.getCollider(colH);
    if (col) col.setCollisionGroups(on ? GROUPS_TOKEN_GHOST : GROUPS_TOKEN);
  }

  /** [Kleber]: the token stops being a projectile and starts being terrain. */
  freeze(handle: number): void {
    const b = this.world.getRigidBody(handle);
    if (!b) return;
    b.setLinvel({ x: 0, y: 0, z: 0 }, false);
    b.setAngvel({ x: 0, y: 0, z: 0 }, false);
    b.setBodyType(RAPIER.RigidBodyType.Fixed, true);
    const colH = this.colliderOfBody.get(handle);
    if (colH !== undefined) {
      const col = this.world.getCollider(colH);
      // stuck tokens join the FIELD layer: they are now phaseable scenery
      if (col) col.setCollisionGroups(GROUPS_FIELD);
      this.fieldColliders.add(colH);
      this.anchorColliders.add(colH); // dams grow themselves
    }
  }

  /**
   * Sleep management. A fixed body is not part of any island, so freezing the buried
   * interior of the pusher pile stops one dropped coin from waking all 300. Unlike
   * freeze() this keeps the collider on the TOKEN layer — a sedimented coin is still
   * a coin, it is just not being integrated this second.
   */
  setSedimented(handle: number, on: boolean): void {
    const b = this.world.getRigidBody(handle);
    if (!b) return;
    if (on) {
      b.setLinvel({ x: 0, y: 0, z: 0 }, false);
      b.setAngvel({ x: 0, y: 0, z: 0 }, false);
      b.setBodyType(RAPIER.RigidBodyType.Fixed, false);
    } else {
      b.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
      b.wakeUp();
    }
  }

  impulse(handle: number, v: Vec3): void {
    const b = this.world.getRigidBody(handle);
    if (b && b.bodyType() === RAPIER.RigidBodyType.Dynamic) b.applyImpulse(v, true);
  }

  setLinvel(handle: number, v: Vec3): void {
    const b = this.world.getRigidBody(handle);
    if (b) b.setLinvel(v, true);
  }

  /** NOTE: the returned vector is already a fresh object from the Rapier wrapper —
   *  do not copy it again, that doubles the per-tick allocation count. Treat read-only. */
  translation(handle: number): Vec3 {
    const b = this.world.getRigidBody(handle);
    return b ? b.translation() : { x: 0, y: 0, z: 0 };
  }

  linvel(handle: number): Vec3 {
    const b = this.world.getRigidBody(handle);
    return b ? b.linvel() : { x: 0, y: 0, z: 0 };
  }

  rotation(handle: number): [number, number, number, number] {
    const b = this.world.getRigidBody(handle);
    if (!b) return [0, 0, 0, 1];
    const q = b.rotation();
    return [q.x, q.y, q.z, q.w];
  }

  isSleeping(handle: number): boolean {
    const b = this.world.getRigidBody(handle);
    return b ? b.isSleeping() : true;
  }

  mass(handle: number): number {
    const b = this.world.getRigidBody(handle);
    return b ? b.mass() : 0;
  }

  wake(handle: number): void {
    const b = this.world.getRigidBody(handle);
    if (b) b.wakeUp();
  }

  setTranslation(handle: number, p: Vec3): void {
    const b = this.world.getRigidBody(handle);
    if (b) b.setTranslation(p, true);
  }

  /** Count of bodies Rapier is actually integrating — the perf story in one number. */
  awakeCount(bodies: readonly number[]): number {
    let n = 0;
    for (const h of bodies) if (!this.isSleeping(h)) n++;
    return n;
  }

  // ------------------------------------------------------------ stepping

  step(): CollisionEvt[] {
    this.world.step(this.eq);

    const forces = new Map<string, number>();
    this.eq.drainContactForceEvents((ev) => {
      const c1 = ev.collider1();
      const c2 = ev.collider2();
      forces.set(pairKey(c1, c2), ev.totalForceMagnitude());
    });

    const out: CollisionEvt[] = [];
    this.eq.drainCollisionEvents((c1, c2, started) => {
      if (!started) return;
      const b1 = this.bodyOfCollider.get(c1);
      const b2 = this.bodyOfCollider.get(c2);
      if (b1 === undefined || b2 === undefined) return;
      const anchor =
        (b1 === -1 && this.anchorColliders.has(c1)) || (b2 === -1 && this.anchorColliders.has(c2));
      out.push({
        a: b1,
        b: b2,
        aStatic: b1 === -1,
        bStatic: b2 === -1,
        anchor,
        impulse: forces.get(pairKey(c1, c2)) ?? 0,
      });
    });

    // canonical ordering: cheap insurance against any drain-order surprise
    out.sort((x, y) => x.a - y.a || x.b - y.b);
    return out;
  }

  free(): void {
    this.world.free();
  }
}

function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function staticDesc(shape: StaticShape): RAPIER.ColliderDesc {
  switch (shape.kind) {
    case 'box':
      return RAPIER.ColliderDesc.cuboid(shape.hx, shape.hy, shape.hz);
    case 'cylinder':
      return RAPIER.ColliderDesc.cylinder(shape.halfHeight, shape.radius);
    case 'ball':
      return RAPIER.ColliderDesc.ball(shape.radius);
  }
}
